// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Refund assistance  (route /refund-assistance — the "Refund Assistance" row on the profile page)
// One place for "something is wrong with my order": how refunds really work here, then the customer's recent orders, each with the
// action that applies TO IT — cancel (before ZenFresh confirms; the order page does the refund), report a problem (delivered, inside
// the claim window, via pages/Complaint; a person reviews it and an approved claim is paid as wallet credit), or the refund status of a cancelled order —
// then their claims with status. Nothing is instant or automatic, and no sensor/cold-chain/temperature claim is made.
// Deliberately NOT here (no data/system behind it): "sub-zero shield", "< 4°C" promise, 60-second automatic refunds, temperature logs,
// photo proof, dawn replacement, "Harbor Concierge" chat and response-time claims, eco-box deposits.
// Neighbours: utils/claims (API), utils/orders, pages/OrderDetail (cancel), pages/Wallet (credit lands here), pages/Contact.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, CircleCheck, Clock, Headset, Loader2, ReceiptText, ShieldCheck, Wallet, XCircle } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { fetchClaims, reasonLabel, type Claim, type ClaimList } from "../utils/claims";
import { STATUS_LABEL, fetchOrders, formatWhen, reachedAt, type Order } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";

type Load = { status: "loading" } | { status: "error" } | { status: "ready"; orders: Order[]; claims: ClaimList };

const RECENT_ORDERS = 8;
const chip = "inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold";

function ClaimBadge({ claim }: { claim: Claim }) {
  if (claim.status === "approved") {
    return (
      <span className={`${chip} bg-teal-100 text-teal-700`}>
        <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" /> Credited to wallet
      </span>
    );
  }
  if (claim.status === "rejected") {
    return (
      <span className={`${chip} bg-coral-500/10 text-coral-600 dark:text-coral-300`}>
        <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> Not approved
      </span>
    );
  }
  return (
    <span className={`${chip} bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50`}>
      <Clock className="h-3.5 w-3.5" aria-hidden="true" /> Under review
    </span>
  );
}

/** Whole hours left in the claim window (rounded up), or null when there is no open window. */
function hoursLeft(order: Order, windowHours: number, now: number): number | null {
  const delivered = reachedAt(order, "delivered");
  if (order.status !== "delivered" || !delivered) return null;
  const left = new Date(delivered).getTime() + windowHours * 3_600_000 - now;
  return left > 0 ? Math.ceil(left / 3_600_000) : null;
}

export default function RefundAssistance() {
  useDocumentTitle("Refund assistance");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const refresh = async () => {
    try {
      const [orders, claims] = await Promise.all([fetchOrders(), fetchClaims()]);
      setLoad({ status: "ready", orders: orders.slice(0, RECENT_ORDERS), claims });
    } catch {
      setLoad({ status: "error" });
    }
  };

  useEffect(() => {
    if (signedIn) void refresh();
  }, [signedIn]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const now = Date.now();
  const claimOf = (ref: string) => (load.status === "ready" ? load.claims.claims.find((c) => c.orderRef === ref) : undefined);
  const windowHours = load.status === "ready" ? load.claims.windowHours : 24;

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Refund assistance</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Cancel an order or report a problem</p>
            </div>
          </header>

          <Link to="/coming-soon/instant-refunds" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Instant refunds &amp; freshness inspection</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <section aria-labelledby="how-heading" className="rounded-2xl bg-ocean-900 p-4 text-foam-50 shadow-md">
            <h2 id="how-heading" className="flex items-center gap-2 font-display text-base font-bold">
              <ShieldCheck className="h-5 w-5 text-teal-300" aria-hidden="true" /> How refunds work
            </h2>
            <ul className="mt-2 space-y-2 text-sm text-foam-100/90">
              <li><strong className="text-foam-50">Before we confirm an order</strong> you can cancel it from the order page. The money goes back to the original payment method or to your wallet — you choose.</li>
              <li><strong className="text-foam-50">After delivery</strong> you can report a problem within {windowHours} hours. A person at ZenFresh reviews it; if approved, the amount is credited to your wallet.</li>
            </ul>
          </section>

          {load.status === "loading" && (
            <p role="status" className="mt-4 flex items-center justify-center gap-2 text-ocean-900 dark:text-foam-50">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading your orders…
            </p>
          )}
          {load.status === "error" && (
            <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-semibold text-ocean-950 dark:text-foam-50">We couldn&apos;t load your orders.</p>
              <button type="button" onClick={() => void refresh()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 font-bold text-white">Try again</button>
            </div>
          )}

          {load.status === "ready" && (
            <>
              <section aria-labelledby="orders-heading" className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h2 id="orders-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Select an order</h2>
                  <Link to="/orders" className="text-xs font-bold text-ocean-800 underline dark:text-foam-100">View all orders</Link>
                </div>
                {load.orders.length === 0 && (
                  <div className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
                    <ReceiptText className="mx-auto h-9 w-9 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
                    <p className="mt-2 font-semibold text-ocean-950 dark:text-foam-50">No orders yet</p>
                    <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Once you&apos;ve placed an order, help with it appears here.</p>
                  </div>
                )}
                <ul className="flex flex-col gap-3">
                  {load.orders.map((order) => {
                    const claim = claimOf(order.ref);
                    const left = claim ? null : hoursLeft(order, windowHours, now);
                    return (
                      <li key={order.ref} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10" data-testid="refund-order">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-bold text-ocean-900 dark:text-foam-50">
                            Order #{order.ref} <span className="font-normal text-ocean-800/75 dark:text-foam-100/70">· {STATUS_LABEL[order.status]}</span>
                          </p>
                          {claim ? (
                            <ClaimBadge claim={claim} />
                          ) : left !== null ? (
                            <span className={`${chip} bg-teal-100 text-teal-700`}><CircleCheck className="h-3.5 w-3.5" aria-hidden="true" /> Can report ({left}h left)</span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs text-ocean-800/80 dark:text-foam-100/75">Placed {formatWhen(order.createdAt)}</p>
                        <ul className="mt-2 space-y-1 rounded-xl bg-ocean-700/5 p-3 text-sm dark:bg-white/5">
                          {order.items.map((line) => (
                            <li key={`${line.id}-${line.pack}`} className="flex justify-between gap-3">
                              <span className="min-w-0 truncate">{line.name}</span>
                              <span className="shrink-0 text-ocean-800/80 dark:text-foam-100/75">{line.qty} × {line.pack === "1kg" ? "1 kg" : "500 g"}</span>
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 flex items-baseline justify-between text-sm">
                          <span className="text-ocean-800/80 dark:text-foam-100/75">Order total</span>
                          <span className="font-display text-lg font-bold text-ocean-900 dark:text-foam-50">{formatInr(order.total)}</span>
                        </p>

                        <div className="mt-3">
                          {order.canCancel && (
                            <Link to={`/orders/${order.ref}`} className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-ocean-800 px-4 text-sm font-bold text-white hover:bg-ocean-700">
                              Cancel this order
                            </Link>
                          )}
                          {!order.canCancel && order.active && (
                            <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">On its way — if something is wrong when it arrives, you can report it here within {windowHours} hours of delivery.</p>
                          )}
                          {order.status === "cancelled" && (
                            <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">
                              Cancelled.{" "}
                              {order.refund
                                ? order.refund.status === "failed"
                                  ? "Your refund needs our team to finish it — we'll be in touch, or write to us below."
                                  : order.refund.destination === "wallet"
                                    ? "Your refund was credited to your wallet."
                                    : "Your refund was sent to your original payment method."
                                : order.walletRefunded > 0
                                  ? "The wallet part was credited back to your wallet."
                                  : ""}
                            </p>
                          )}
                          {order.status === "delivered" && !claim && left !== null && (
                            <Link to={`/complaint?order=${encodeURIComponent(order.ref)}`} className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-ocean-800 px-4 text-sm font-bold text-white hover:bg-ocean-700">
                              <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Report a problem
                              <span className="sr-only"> with order {order.ref}</span>
                            </Link>
                          )}
                          {order.status === "delivered" && !claim && left === null && (
                            <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">The {windowHours}-hour window to report a problem has closed. You can still write to us below.</p>
                          )}
                          {claim && <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Reported: {reasonLabel(claim.reason)}. See the status under &ldquo;Your reports&rdquo;.</p>}
                        </div>

                      </li>
                    );
                  })}
                </ul>
              </section>

              <section aria-labelledby="claims-heading" className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h2 id="claims-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Your reports</h2>
                  <span className="text-xs text-ocean-800/80 dark:text-foam-100/75">{load.claims.claims.length} in total</span>
                </div>
                {load.claims.claims.length === 0 && <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">You haven&apos;t reported any problems.</p>}
                <ul className="flex flex-col gap-3">
                  {load.claims.claims.map((claim) => (
                    <li key={claim.orderRef} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10" data-testid="claim-card">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-bold text-ocean-900 dark:text-foam-50">Order #{claim.orderRef}</p>
                        {claim.status === "approved" && <span className="font-display text-base font-bold text-teal-700 dark:text-teal-300">+{formatInr(claim.creditInr)}</span>}
                      </div>
                      <p className="text-xs text-ocean-800/75 dark:text-foam-100/70">Reported {formatWhen(claim.createdAt)}</p>
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm text-ocean-800/90 dark:text-foam-100/85">{reasonLabel(claim.reason)}</span>
                        <ClaimBadge claim={claim} />
                      </div>
                      {claim.note && <p className="mt-2 text-xs text-ocean-800/80 dark:text-foam-100/75">Your note: {claim.note}</p>}
                      {claim.resolutionNote && <p className="mt-2 rounded-lg bg-ocean-700/5 p-2 text-xs text-ocean-900 dark:bg-white/5 dark:text-foam-50">From ZenFresh: {claim.resolutionNote}</p>}
                      {claim.status === "approved" && (
                        <Link to="/wallet" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-ocean-800 underline dark:text-foam-100">
                          <Wallet className="h-3.5 w-3.5" aria-hidden="true" /> See it in your wallet
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}

          <Link
            to={`/contact?subject=${encodeURIComponent("Refund assistance")}`}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white px-5 text-sm font-bold text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-50 dark:ring-white/10"
          >
            <Headset className="h-5 w-5" aria-hidden="true" /> Need more help? Write to us
          </Link>
        </div>
      </div>
    </PageTransition>
  );
}
